import { useEffect, useRef } from 'react'
import { wsClient } from '../services/websocketClient'
import { GameMode, GameState, MultiplayerState, ServerMessage, GameAction, PROTOCOL_VERSION, RoomListEntry, LobbyChatMessage, GamePhase } from '../types'
import * as gameLogic from '../services/gameLogic'

const shouldKeepWsConnection = (gameMode: GameMode, hasGameState: boolean) => (
  gameMode === 'MAIN_MENU' ||
  gameMode === 'ROOM_LOBBY' ||
  gameMode === 'MULTIPLAYER_MENU' ||
  gameMode === 'PLAYING' ||
  gameMode === 'CREATE_ROOM' ||
  gameMode === 'JOIN_ROOM' ||
  (gameMode === 'OPTIONS' && hasGameState)
)

const isProtocolMatch = (version: string | number) => Number(version) === PROTOCOL_VERSION
const DEDUPE_WINDOW_MS = 8000

const preserveLocalPreparedRotations = (snapshot: GameState, current: GameState | null, localPlayerNumber: 1 | 2 | null): GameState => {
  if (!current || !localPlayerNumber) return snapshot
  const currentPlayer = current.players.find(player => player.id === localPlayerNumber)
  if (!currentPlayer) return snapshot
  const rotations = new Map(currentPlayer.prepared.map(card => [card.card.id, card.rotation]))
  const currentHandOrder = currentPlayer.hand.map(card => card.id)

  return {
    ...snapshot,
    players: snapshot.players.map(player => player.id === localPlayerNumber
      ? {
          ...player,
          hand: (() => {
            const snapshotCards = new Map(player.hand.map(card => [card.id, card]))
            const orderedCards = currentHandOrder
              .map(cardId => snapshotCards.get(cardId))
              .filter((card): card is typeof player.hand[number] => !!card)
            const orderedIds = new Set(orderedCards.map(card => card.id))
            return [...orderedCards, ...player.hand.filter(card => !orderedIds.has(card.id))]
          })(),
          prepared: player.prepared.map(card => ({
            ...card,
            rotation: snapshot.round === current.round
              ? rotations.get(card.card.id) ?? card.rotation
              : card.rotation,
          })),
        }
      : player) as GameState['players'],
  }
}

type LastRoomSession = {
  roomCode?: string
  playerName: string
  password?: string
  token?: string
}

export const useMultiplayer = (
  gameMode: GameMode,
  gameState: GameState | null,
  applyGameAction: (state: GameState, action: GameAction) => GameState | null,
  setGameState: (u: (prev: GameState | null) => GameState | null) => void,
  setMultiplayerState: (u: (prev: MultiplayerState) => MultiplayerState) => void,
  setWsError: (v: string | null) => void,
  setLastOpponentAction: (v: string | null) => void,
  handleNavigate: (mode: GameMode) => void,
  onPeerDayReady?: (playerNumber: 1 | 2) => void,
  onRoomListUpdate?: (rooms: RoomListEntry[]) => void,
  onChatMessage?: (msg: LobbyChatMessage) => void,
) => {

  // Use refs to hold the latest versions of callbacks/props to avoid re-subscribing
  const refs = useRef({
    applyGameAction,
    setGameState,
    setMultiplayerState,
    setWsError,
    setLastOpponentAction,
    handleNavigate,
    onPeerDayReady,
    onRoomListUpdate,
    onChatMessage
  })

  // Track processed actions with timestamps for time-based deduplication
  const processedActionKeys = useRef<Map<string, number>>(new Map())
  const currentGameStateRef = useRef<GameState | null>(gameState)
  const reconnectDuringPlayRef = useRef(false)
  const currentLocalPlayerNumberRef = useRef<1 | 2 | null>(null)
  const pendingHostResyncAfterJoinRef = useRef(false)
  const lastRoomSessionRef = useRef<LastRoomSession | null>(null)
  const reconnectJoinInFlightRef = useRef(false)
  const latestStateVersionRef = useRef(0)

  useEffect(() => {
    currentGameStateRef.current = gameState
  }, [gameState])

  useEffect(() => {
    refs.current = {
      applyGameAction,
      setGameState,
      setMultiplayerState,
      setWsError,
      setLastOpponentAction,
      handleNavigate,
      onPeerDayReady,
      onRoomListUpdate,
      onChatMessage
    }
  })

  useEffect(() => {
    const tryReconnect = () => {
      const shouldStayConnected = shouldKeepWsConnection(gameMode, !!currentGameStateRef.current)
      if (!shouldStayConnected || wsClient.isConnected()) return
      console.log('[Multiplayer] Visibility/focus restore: reconnecting websocket')
      wsClient.connect()
    }

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') tryReconnect()
    }

    window.addEventListener('focus', tryReconnect)
    document.addEventListener('visibilitychange', onVisibilityChange)
    window.addEventListener('online', tryReconnect)

    return () => {
      window.removeEventListener('focus', tryReconnect)
      document.removeEventListener('visibilitychange', onVisibilityChange)
      window.removeEventListener('online', tryReconnect)
    }
  }, [gameMode])

  useEffect(() => {
    if (gameMode !== 'PLAYING') return
    const interval = setInterval(() => {
      const state = currentGameStateRef.current
      if (!state || !wsClient.isConnected()) return
      try {
        wsClient.send({
          type: 'STATE_CHECKSUM',
          checksum: gameLogic.computeStateChecksum(state, currentLocalPlayerNumberRef.current ?? undefined),
          version: PROTOCOL_VERSION
        })
      } catch { }
    }, 5000)
    return () => clearInterval(interval)
  }, [gameMode])

  // Protect against legacy clients or retransmitted room messages applying an action twice.
  const getActionKey = (action: GameAction): string => `${action.type}-${action.playerId}-${JSON.stringify(action)}`

  const pruneProcessedActions = (now: number) => {
    if (processedActionKeys.current.size <= 50) return
    for (const [key, timestamp] of processedActionKeys.current.entries()) {
      if ((now - timestamp) > DEDUPE_WINDOW_MS) {
        processedActionKeys.current.delete(key)
      }
    }
  }

  const isDuplicateAction = (action: GameAction): boolean => {
    const now = Date.now()

    // Create a unique key for this action based on its content
    const actionKey = getActionKey(action)

    // Check if this key was seen recently
    const lastSeen = processedActionKeys.current.get(actionKey)
    if (lastSeen && (now - lastSeen) < DEDUPE_WINDOW_MS) {
      console.log(`[Multiplayer] Skipping duplicate action:`, action.type, `(seen ${now - lastSeen}ms ago)`)
      return true
    }

    pruneProcessedActions(now)

    return false
  }

  const markProcessedAction = (action: GameAction) => {
    const now = Date.now()
    processedActionKeys.current.set(getActionKey(action), now)
    pruneProcessedActions(now)
  }

  const didActionChangeState = (prev: GameState, next: GameState | null): next is GameState => {
    if (!next) return false
    try {
      return gameLogic.computeStateChecksum(prev) !== gameLogic.computeStateChecksum(next)
    } catch {
      return next !== prev
    }
  }

  useEffect(() => {
    const handleWebSocketMessage = (message: ServerMessage) => {
      console.log('[Multiplayer] Received message:', message.type);
      const {
        applyGameAction,
        setGameState,
        setMultiplayerState,
        setWsError,
        setLastOpponentAction,
        handleNavigate,
        onPeerDayReady,

        onRoomListUpdate,
        onChatMessage
      } = refs.current

      switch (message.type) {
        case 'ROOM_CREATED': {
          currentLocalPlayerNumberRef.current = null
          pendingHostResyncAfterJoinRef.current = false
          reconnectJoinInFlightRef.current = false
          latestStateVersionRef.current = 0
          if (lastRoomSessionRef.current) {
            lastRoomSessionRef.current.roomCode = message.roomCode
          }
          setMultiplayerState(prev => ({
            ...prev,
            localPlayerId: message.playerId,
            roomInfo: { code: message.roomCode, roomName: message.roomName, hostId: message.hostId, players: message.players },
          }))
          handleNavigate('ROOM_LOBBY')
          setWsError(null)
          break
        }
        case 'ROOM_JOINED': {
          console.log('[Multiplayer] ROOM_JOINED received:', message);
          const isGameReconnect = message.playerNumber != null;
          if (isGameReconnect) {
            currentLocalPlayerNumberRef.current = message.playerNumber as 1 | 2
            reconnectJoinInFlightRef.current = false
            pendingHostResyncAfterJoinRef.current = false
          } else {
            currentLocalPlayerNumberRef.current = null
            pendingHostResyncAfterJoinRef.current = false
            latestStateVersionRef.current = 0
          }
          if (lastRoomSessionRef.current) {
            lastRoomSessionRef.current.roomCode = message.roomCode
          }
          setMultiplayerState(prev => ({
            ...prev,
            localPlayerId: message.playerId,
            localPlayerNumber: isGameReconnect ? message.playerNumber! : prev.localPlayerNumber,
            roomInfo: { code: message.roomCode, roomName: message.roomName, hostId: message.hostId, players: message.players },
            opponentConnectionStatus: message.players.length < 2 ? 'offline' : 'online',
          }))
          // On game reconnect, don't navigate to lobby — RESYNC_STATE will navigate to PLAYING
          if (!isGameReconnect) {
            handleNavigate('ROOM_LOBBY')
          }
          if (isGameReconnect) {
            try { wsClient.send({ type: 'REQUEST_LATEST_STATE' }) } catch { }
          }
          setWsError(null)
          break
        }
        case 'SIGNAL_OFFER':
        case 'SIGNAL_ANSWER':
        case 'SIGNAL_ICE': {
          break
        }
        case 'PLAYER_JOINED': {
          setMultiplayerState(prev => ({
            ...prev,
            roomInfo: prev.roomInfo ? {
              ...prev.roomInfo,
              hostId: message.hostId || prev.roomInfo.hostId,
              players: message.players || [...prev.roomInfo.players.filter(p => p.id !== message.player.id), message.player]
            } : null,
            opponentConnectionStatus: 'online',
          }))
          break
        }
        case 'PLAYER_LEFT': {
          // If the HOST left but room is NOT closed, it means they disconnected temporarily.
          // We should NOT exit the game.
          setMultiplayerState(prev => ({
            ...prev,
            roomInfo: prev.roomInfo ? { ...prev.roomInfo, players: message.remainingPlayers, hostId: message.newHostId || prev.roomInfo.hostId } : null,
            opponentConnectionStatus: 'offline',
          }))
          // If we are in Game, we might want to pause or show a notification?
          // For now, the game state remains, just the opponent might be 'offline' in the list.
          break
        }
        case 'READY_UPDATE': {
          setMultiplayerState(prev => ({
            ...prev,
            roomInfo: prev.roomInfo ? { ...prev.roomInfo, players: message.players } : null,
          }))
          break
        }
        case 'GAME_STARTED': {
          currentLocalPlayerNumberRef.current = message.playerNumber
          latestStateVersionRef.current = message.stateVersion ?? 1
          setMultiplayerState(prev => ({ ...prev, localPlayerNumber: message.playerNumber, gameSeed: message.gameSeed }))
          const playerData = message.players.map(p => ({ name: p.name, hero: p.hero, playerClass: p.playerClass, famePoints: p.fame || 0 })) as [{ name: string, hero?: any, playerClass?: any, famePoints: number }, { name: string, hero?: any, playerClass?: any, famePoints: number }]
          setGameState(() => {
            const state = gameLogic.initGame(message.gameSeed, playerData)
            // CRITICAL: Ensure neither player is marked as AI in PvP multiplayer
            // This prevents stalling issues if transitioning from Solo mode without page refresh
            state.players[0].isAI = false
            state.players[1].isAI = false
            // Pass fame from server to game state
            state.players[0].famePoints = playerData[0].famePoints
            state.players[1].famePoints = playerData[1].famePoints
            // Load battleHistory from server message (history of games between these two players)
            if (message.battleHistory && Array.isArray(message.battleHistory)) {
              state.battleHistory = message.battleHistory as any
              console.log(`[Multiplayer] Loaded battleHistory from server: ${message.battleHistory.length} prior games between these players`)
            } else {
              // Fallback to localStorage if server doesn't provide history
              try {
                const storedHistory = localStorage.getItem('battleHistory')
                if (storedHistory) {
                  const parsed = JSON.parse(storedHistory)
                  if (Array.isArray(parsed)) {
                    state.battleHistory = parsed
                    console.log(`[Multiplayer] Loaded battleHistory from localStorage: ${parsed.length} entries`)
                  }
                }
              } catch (e) {
                console.warn('[Multiplayer] Failed to load battleHistory from localStorage:', e)
              }
            }
            return state
          })
          handleNavigate('PLAYING')
          break
        }
        case 'GAME_ACTION': {
          console.log(`[Multiplayer] Received GAME_ACTION from WebSocket:`, message.action.type, message.action);
          const incomingStateVersion = message.stateVersion ?? 0
          if (incomingStateVersion > 0 && incomingStateVersion <= latestStateVersionRef.current) {
            console.log(`[Multiplayer] Ignoring stale GAME_ACTION stateVersion=${incomingStateVersion}; latest=${latestStateVersionRef.current}`)
            break
          }

          // Ignore duplicate actions before applying them to the local projection.
          if (isDuplicateAction(message.action)) {
            break
          }

          const actionMessages: Record<string, string> = {
            CHOOSE_TOWER: 'Opponent chose a tower',
            CHOOSE_STASH: 'Opponent chose a power',
            PREPARE_CARD: 'Opponent prepared a card',
            RETURN_CARD: 'Opponent returned a card',
            ATTEMPT_ACTIVATION: 'Opponent activated a card',
            COUNTER_ACTIVATION: 'Opponent countered',
            ACCEPT_ACTIVATION: 'Opponent accepted',
            PASS_TURN: 'Opponent passed',
          }
          setLastOpponentAction(actionMessages[message.action.type] || 'Opponent acted')
          setTimeout(() => setLastOpponentAction(null), 2000)
          setGameState(prev => {
            if (!prev) return prev
            console.log(`[Multiplayer] Applying action to state. Current phase:`, prev.phase, `PicksRemaining:`, prev.stashChoicePicksRemaining);
            const ns = applyGameAction(prev, message.action)
            const applied = message.action.type === 'ROTATE_PREPARED_CARD'
              ? ns !== prev
              : didActionChangeState(prev, ns)
            if (applied) {
              markProcessedAction(message.action)
              if (incomingStateVersion > 0) latestStateVersionRef.current = incomingStateVersion
              console.log(`[Multiplayer] Action applied successfully. New phase:`, ns.phase, `PicksRemaining:`, ns.stashChoicePicksRemaining);
              try {
                wsClient.send({
                  type: 'STATE_CHECKSUM',
                  checksum: gameLogic.computeStateChecksum(ns, currentLocalPlayerNumberRef.current ?? undefined),
                  version: PROTOCOL_VERSION,
                })
              } catch { }
            } else {
              console.log(`[Multiplayer] Action did not change local state; requesting latest server state`);
              try { wsClient.send({ type: 'REQUEST_LATEST_STATE' }) } catch { }
            }
            return ns ?? prev
          })
          break
        }
        case 'DAY_READY': {
          const currentRound = currentGameStateRef.current?.round
          if (message.round != null && currentRound != null && message.round !== currentRound) {
            console.log(`[Multiplayer] Ignoring stale DAY_READY for round ${message.round}; current round is ${currentRound}`)
            break
          }
          if (typeof onPeerDayReady === 'function') onPeerDayReady(message.playerNumber)
          break
        }
        case 'PHASE_CHANGE': {
          if (currentLocalPlayerNumberRef.current !== 1 && message.phase === GamePhase.Combat) {
            setGameState(prev => {
              if (!prev || prev.phase === GamePhase.Combat) return prev
              if (prev.phase === GamePhase.Preparation && prev.round === message.round) {
                console.log('[Multiplayer] Applying PHASE_CHANGE to Combat from host');
                return gameLogic.startCombat(prev)
              }
              return prev
            })
          }
          break
        }
        case 'STATE_CHECKSUM': {
          setGameState(prev => {
            if (!prev) return prev
            const local = gameLogic.computeStateChecksum(prev, currentLocalPlayerNumberRef.current ?? undefined)
            if (local !== message.checksum && isProtocolMatch(message.version)) {
              console.log(`[Multiplayer] Checksum mismatch! Local: ${local}, Remote: ${message.checksum}`);
              console.log(`[Multiplayer] Requesting latest authoritative server state after checksum mismatch.`);
              try { wsClient.send({ type: 'REQUEST_LATEST_STATE' }) } catch { }
            }
            return prev
          })
          break
        }
        case 'RESYNC_STATE': {
          console.log('[Multiplayer] RESYNC_STATE received, version:', message.version, 'expected:', PROTOCOL_VERSION);
          if (!isProtocolMatch(message.version)) {
            console.warn('[Multiplayer] RESYNC_STATE version mismatch! Got:', message.version, 'Expected:', PROTOCOL_VERSION, 'Types:', typeof message.version, typeof PROTOCOL_VERSION);
            break;
          }
          // Validate snapshot has required fields before applying
          if (!message.snapshot || !message.snapshot.players || !Array.isArray(message.snapshot.players) || !message.snapshot.activations) {
            console.warn('[Multiplayer] Received invalid RESYNC_STATE snapshot, ignoring', message.snapshot)
            break
          }
          const incomingStateVersion = message.stateVersion ?? 0
          if (incomingStateVersion > 0) {
            latestStateVersionRef.current = incomingStateVersion
          }
          console.log('[Multiplayer] RESYNC_STATE valid, navigating to PLAYING');
          
          // Add a heartbeat to ensure UI re-renders and effects trigger
          const preservedSnapshot = preserveLocalPreparedRotations(
            message.snapshot,
            currentGameStateRef.current,
            currentLocalPlayerNumberRef.current
          )
          const snapshot = {
            ...preservedSnapshot,
            _resyncHeartbeat: Date.now()
          };
          setGameState(() => snapshot)

          if (currentLocalPlayerNumberRef.current == null && message.playerNumber != null) {
            currentLocalPlayerNumberRef.current = message.playerNumber;
            setMultiplayerState(prev => ({
               ...prev,
               localPlayerNumber: message.playerNumber
            }));
          }

          handleNavigate('PLAYING')
          break
        }
        case 'ERROR': {
          console.error('[Multiplayer] Server ERROR:', message.message);
          // Map technical error codes to translation keys
          const errorMap: Record<string, string> = {
            'SESSION_ALREADY_ACTIVE': 'error.sessionAlreadyActive',
          };
          const displayError = errorMap[message.message] || message.message;
          setWsError(displayError)
          setTimeout(() => setWsError(null), 3100)
          break
        }
        case 'ROOM_LIST': {
          if (typeof onRoomListUpdate === 'function') {
            onRoomListUpdate(message.rooms)
          }
          break
        }
        case 'CHAT_MESSAGE': {
          if (typeof onChatMessage === 'function') {
            onChatMessage(message.message)
          }
          break
        }
        case 'OPPONENT_CONNECTION_STATUS': {
          setMultiplayerState(prev => ({ ...prev, opponentConnectionStatus: message.status }))
          if (message.status === 'offline') {
            setLastOpponentAction('Opponent disconnected - match is preserved')
            setTimeout(() => setLastOpponentAction(null), 2500)
          } else if (message.status === 'online') {
            setLastOpponentAction('Opponent reconnected')
            setTimeout(() => setLastOpponentAction(null), 2000)
          }
          break
        }
        case 'TECHNICAL_VICTORY': {
          console.log('[Multiplayer] TECHNICAL_VICTORY received:', message);
          setGameState(prev => {
            if (!prev) return prev;
            return {
              ...prev,
              phase: GamePhase.GameOver,
              winner: prev.players.find(p => p.id === message.winnerId) || null,
              pendingVictory: {
                winnerId: message.winnerId,
                reportedFameDelta: message.delta,
                canAssignPoint: message.canAssignPoint,
                isTechnical: true
              }
            };
          });
          break;
        }
        case 'ROOM_CLOSED': {
          currentLocalPlayerNumberRef.current = null
          pendingHostResyncAfterJoinRef.current = false
          lastRoomSessionRef.current = null
          reconnectJoinInFlightRef.current = false
          // Host left permanently or room destroyed
          const isHostClosure =
            message.reason === 'HOST_DISCONNECTED' ||
            message.reason === 'HOST_LEFT_THE_GAME' ||
            message.reason === 'HOST_LEFT_THE_ROOM' ||
            message.reason === 'Host left the room';
          setMultiplayerState(prev => ({
            ...prev,
            roomInfo: null,
            localPlayerId: null,
            localPlayerNumber: null,
          }))
          handleNavigate(isHostClosure ? 'MULTIPLAYER_MENU' : 'JOIN_ROOM')
          // Map technical reason codes to translation keys
          const reasonMap: Record<string, string> = {
            'OPPONENT_DISCONNECT_TIMEOUT': 'multiplayer.opponentDisconnectTimeout',
            'HOST_DISCONNECTED': 'multiplayer.hostDisconnected',
            'HOST_LEFT_THE_GAME': 'multiplayer.hostDisconnected',
            'HOST_LEFT_THE_ROOM': 'multiplayer.hostDisconnected',
            'OPPONENT_LEFT_THE_GAME': 'multiplayer.opponentDisconnectTimeout',
          };
          const displayReason = reasonMap[message.reason] || message.reason;
          setWsError(displayReason)
          setTimeout(() => setWsError(null), 4000)
          break
        }
      }
    }

    // Include MAIN_MENU to support auto-join on page refresh
    const shouldConnect = shouldKeepWsConnection(gameMode, !!currentGameStateRef.current)
    if (!shouldConnect) {
      wsClient.disconnect()
      setMultiplayerState(prev => ({ ...prev, connectionStatus: 'DISCONNECTED' }))
      return
    }
    wsClient.connect()
    console.log('[Multiplayer] Registering message handler for gameMode:', gameMode);
    const unsubStatus = wsClient.onStatusChange(status => {
      refs.current.setMultiplayerState(prev => ({ ...prev, connectionStatus: status }))
      if (status === 'RECONNECTING' || status === 'DISCONNECTED') {
        if (gameMode === 'PLAYING') {
          reconnectDuringPlayRef.current = true
          pendingHostResyncAfterJoinRef.current = true
          reconnectJoinInFlightRef.current = false
        }
      }
      if (status === 'CONNECTED' && reconnectDuringPlayRef.current) {
        const session = lastRoomSessionRef.current
        if (session?.roomCode && session.token && !reconnectJoinInFlightRef.current) {
          reconnectJoinInFlightRef.current = true
          console.log('[Multiplayer] WebSocket restored: rejoining room', session.roomCode)
          try {
            wsClient.send({
              type: 'JOIN_ROOM',
              roomCode: session.roomCode,
              playerName: session.playerName,
              password: session.password,
              token: session.token
            })
          } catch (e) {
            reconnectJoinInFlightRef.current = false
            console.warn('[Multiplayer] Failed to send reconnect JOIN_ROOM:', e)
          }
        } else if (!session?.token) {
          console.warn('[Multiplayer] Cannot auto-rejoin without a persistent token')
        }
        reconnectDuringPlayRef.current = false
      }
    })
    const unsubMessage = wsClient.onMessage(msg => handleWebSocketMessage(msg))
    return () => {
      console.log('[Multiplayer] Unregistering message handler for gameMode:', gameMode);
      unsubStatus()
      unsubMessage()
    }
  }, [gameMode]) // Keep connection alive for in-game OPTIONS

  const createRoom = (playerName: string, roomName: string, password?: string, token?: string) => {
    lastRoomSessionRef.current = { playerName, password, token }
    wsClient.send({ type: 'CREATE_ROOM', playerName, roomName, password, token })
  }
  const joinRoom = (roomCode: string, playerName: string, password?: string, token?: string) => {
    console.log('[Multiplayer] joinRoom called:', { roomCode, playerName, token: token?.substring(0, 8) + '...' });
    lastRoomSessionRef.current = { roomCode, playerName, password, token }
    wsClient.send({ type: 'JOIN_ROOM', roomCode, playerName, password, token })
  }
  const leaveRoom = (intentional: boolean = false) => {
    if (intentional) {
      lastRoomSessionRef.current = null
      reconnectDuringPlayRef.current = false
      reconnectJoinInFlightRef.current = false
    }
    wsClient.send({ type: 'LEAVE_ROOM', intentional })
  }
  const toggleReady = () => {
    wsClient.send({ type: 'TOGGLE_READY' })
  }
  const startGame = () => {
    wsClient.send({ type: 'START_GAME' })
  }
  const requestRoomList = () => {
    wsClient.send({ type: 'REQUEST_ROOM_LIST' })
  }
  const sendChatMessage = (text: string) => {
    wsClient.send({ type: 'CHAT_MESSAGE', text })
  }

  return { createRoom, joinRoom, leaveRoom, toggleReady, startGame, requestRoomList, sendChatMessage }
}
